import { FusionError, serializeFusionError, serializePrettyJsonFusionError } from "@fusion-advantage/backcompat"
import { sqlQuery, sqlRun } from "./ldb"
import { err, ok, Result } from "neverthrow"
import { LdbBlobStorageEncoding, ldbReadBlobData, ldbReadBlobPrettyJson, ldbWriteBlobData } from "./LdbBlobData"
import { SyncItemKey, SyncItemType } from "../redux/syncthunk/SyncItemResult"
import { UnvalidatedCodeNext } from "@fusion-advantage/backcompat"
import { CloudToDeviceBlobUpnAll } from "./ldbCloudToDeviceBlob"

export enum DeviceToCloudBlobType {
	EncounterFile = 'encounterFile',
	EncounterStatus = 'encounterStatus',
	Import = 'import',
}

export type DeviceToCloudBlobDiscriminator = {
	type: DeviceToCloudBlobType.EncounterFile
	encounterGuid: string
	fileId: string
	upn: string
} | {
	type: DeviceToCloudBlobType.EncounterStatus
	encounterGuid: string
	fileId: null
	upn: string
} | {
	type: DeviceToCloudBlobType.Import
	encounterGuid: string
	fileId: null
	upn: string
}

export type DeviceToCloudBlob = DeviceToCloudBlobDiscriminator & {
	data: Uint8Array
} & (
	{
		type: DeviceToCloudBlobType.EncounterFile
		nextCodeNext: null
		contentType: string
	} | {
		type: DeviceToCloudBlobType.EncounterStatus
		nextCodeNext: string
		contentType: null
	} | {
		type: DeviceToCloudBlobType.Import
		nextCodeNext: null
		contentType: null
	}
)

type DeviceToCloudWriteBlob = DeviceToCloudBlob & (
	{
		type: DeviceToCloudBlobType.EncounterFile
		bin: true
	} | {
		type: DeviceToCloudBlobType.Import|DeviceToCloudBlobType.EncounterStatus
		bin: false
	}
)

export const insertDeviceToCloudBlob = async (blob: DeviceToCloudWriteBlob): Promise<Result<void, FusionError>> => {
	const rData = ldbWriteBlobData(blob.data, blob.bin)
	if(rData.isErr()) {
		return err(serializeFusionError('insertDeviceToCloudBlob', rData.error))
	}
	let r = await sqlRun(
		`
			insert into deviceToCloudBlob (type, encounterGuid, fileId, upn, data, encoding, nextCodeNext, contentType, createdAt)
			values (?, ?, ?, ?, ?, ?, ?, ?, unixepoch())
		`,
		[
			blob.type,
			blob.encounterGuid,
			blob.fileId,
			blob.upn,
			rData.value.data,
			rData.value.encoding,
			blob.nextCodeNext,
			blob.contentType,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('insertDeviceToCloudBlob', r.error))
	}
	return ok()
}

export type DeviceToCloudOrderedBlob = DeviceToCloudBlob & {
	ordinal: number
	createdAt: number
	syncedAt: number|null
}

export const selectDeviceToCloudBlob = async (ordinal: number): Promise<Result<DeviceToCloudOrderedBlob|null, FusionError>> => {
	let r = await sqlQuery<
		DeviceToCloudOrderedBlob & {
			encoding: LdbBlobStorageEncoding
			data: string|Uint8Array
		}
	>(
		`
			select ordinal, type, encounterGuid, fileId, upn, encoding, nextCodeNext, contentType, createdAt, syncedAt,
			  case when encoding = '${LdbBlobStorageEncoding.B64Bin}' then cast (data as text)
			  else data
			   end as data
			  from deviceToCloudBlob
			 where ordinal = ?
		`,
		[
			ordinal,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('selectDeviceToCloudBlob', r.error))
	}
	if(r.value.length < 1) {
		return ok(null)
	}
	const row = r.value[0]
	const rData = ldbReadBlobData(row.data, row.encoding)
	if(rData.isErr()) {
		return err(serializeFusionError('selectDeviceToCloudBlob', rData.error))
	}
	return ok({
		...row,
		data: rData.value,
	})
}

export const selectLatestDeviceToCloudBlob = async (params: DeviceToCloudBlobDiscriminator): Promise<Result<DeviceToCloudOrderedBlob|null, FusionError>> => {
	// using `is` in the query below so that a "fileId is null" query works
	// 20260425 select where upn = '*' to access migrated files
	// that were previously stored this way
	const rKey = await sqlQuery<{ordinal: number}>(
		`
			select ordinal
			  from deviceToCloudBlob
			 where type is ?
			   and encounterGuid is ?
			   and fileId is ?
			   and upn in (?, '${CloudToDeviceBlobUpnAll}')
			 order by ordinal desc
			 limit 1
		`,
		[
			params.type,
			params.encounterGuid,
			params.fileId,
			params.upn,
		],
	)
	if(rKey.isErr()) {
		return err(serializeFusionError('selectLatestDeviceToCloudBlob', rKey.error))
	}
	if(rKey.value.length < 1) {
		return ok(null)
	}
	return selectDeviceToCloudBlob(rKey.value[0].ordinal)
}

export interface DeviceToCloudEncounterStatus {
	nextCodeNext: UnvalidatedCodeNext
	createdAt: number
}

type selectDeviceToCloudEncounterStatusChangesOk = Map<string, DeviceToCloudEncounterStatus>

export const selectDeviceToCloudEncounterStatusChanges = async (upn: string): Promise<Result<selectDeviceToCloudEncounterStatusChangesOk, FusionError>> => {
	const r = await sqlQuery<{
		encounterGuid: string
		nextCodeNext: UnvalidatedCodeNext|null
		createdAt: number
	}>(
		`
			select encounterGuid, nextCodeNext, createdAt
			  from deviceToCloudBlob
			 where upn = ?
			   and type = ?
			 order by ordinal desc
		`,
		[
			upn,
			DeviceToCloudBlobType.EncounterStatus,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('selectDeviceToCloudEncounterStatusChanges', r.error))
	}
	const changes = new Map<string, DeviceToCloudEncounterStatus>()
	for(let {encounterGuid, nextCodeNext, createdAt} of r.value) {
		if(!nextCodeNext) {
			return err(serializeFusionError('selectDeviceToCloudEncounterStatusChanges', `expected CodeNext for '${encounterGuid}' but null`))
		}
		// only consider the latest change for each encounter
		if(!changes.has(encounterGuid)) {
			changes.set(
				encounterGuid,
				{
					nextCodeNext,
					createdAt,
				}
			)
		}
	}
	return ok(changes)
}

export const selectDeviceToCloudBlobSyncItemsCount = async (): Promise<Result<number, FusionError>> => {
	const r = await sqlQuery<{count: number}>(
		`
			select count(*) as count
			  from deviceToCloudBlob
			 where syncedAt is null
		`
	)
	if(r.isErr()) {
		return err(serializeFusionError('selectDeviceToCloudBlobSyncItemsCount', r.error))
	}
	return ok(r.value[0].count)
}

export interface DeviceToCloudBlobSyncItem {
	ordinal: number
	itemKey: SyncItemKey
	additionalPendingForEncounter?: boolean
}

interface DeviceToCloudBlobSyncItems {
	batches: DeviceToCloudBlobSyncItem[][]
	ordinals: number[]
}

export const selectDeviceToCloudBlobSyncItems = async (): Promise<Result<DeviceToCloudBlobSyncItems, FusionError>> => {
	// for convenience of types we are selecting `upn` here
	// but any sync will sync for all upns
	// in case a different previous user logged out before completing
	// a full cloud sync
	let r = await sqlQuery<{ordinal: number} & DeviceToCloudBlobDiscriminator>(
		`
			select ordinal, type, encounterGuid, fileId, upn
			  from deviceToCloudBlob
			 where syncedAt is null
			 order by ordinal asc
		`
	)
	if(r.isErr()) {
		return err(serializeFusionError('selectDeviceToCloudBlobSyncItems', r.error))
	}
	// per conversation with dan, we want to make sure imports and statuses
	// occur in series order for a given encounterGuid
	// for the rest of it, sequence doesn't matter
	const encounterGuidMaxBatch = new Map<string, number>()
	for(let {type, encounterGuid} of r.value) {
		switch(type) {
			case DeviceToCloudBlobType.EncounterStatus:
			case DeviceToCloudBlobType.Import:
				encounterGuidMaxBatch.set(encounterGuid, (encounterGuidMaxBatch.get(encounterGuid) ?? 0) + 1)
				break
		}
	}
	const batches: DeviceToCloudBlobSyncItem[][] = [[]]
	const encounterGuidNextBatch = new Map<string, number>()
	const getEncounterGuidNextBatch = (encounterGuid: string): [number, boolean] => {
		const batch = encounterGuidNextBatch.get(encounterGuid) ?? 1
		encounterGuidNextBatch.set(encounterGuid, batch + 1)
		while(batches.length <= batch) {
			batches.push([])
		}
		return [batch, batch !== encounterGuidMaxBatch.get(encounterGuid)]
	}
	const ordinals: number[] = []
	for(let row of r.value) {
		let { ordinal, encounterGuid } = row
		ordinals.push(row.ordinal)
		switch(row.type) {
			case DeviceToCloudBlobType.EncounterFile:
				batches[0].push({
					ordinal,
					itemKey: {
						type: SyncItemType.DeviceToCloudFile,
						fileId: row.fileId,
					},
				})
				break
			case DeviceToCloudBlobType.EncounterStatus:
				{
					let [batch, additionalPendingForEncounter] = getEncounterGuidNextBatch(encounterGuid)
					batches[batch].push({
						ordinal,
						itemKey: {
							type: SyncItemType.EncounterStatus,
							encounterGuid,
							ordinal,
						},
						additionalPendingForEncounter,
					})
				}
				break
			case DeviceToCloudBlobType.Import:
				{
					let [batch, additionalPendingForEncounter] = getEncounterGuidNextBatch(encounterGuid)
					batches[batch].push({
						ordinal,
						itemKey: {
							type: SyncItemType.Import,
							encounterGuid,
							ordinal,
						},
						additionalPendingForEncounter,
					})
				}
				continue
		}
	}
	return ok({
		batches,
		ordinals,
	})
}

export const updateDeviceToCloudBlobSynced = async (ordinal: number): Promise<Result<void, FusionError>> => {
	const r = await sqlRun(
		`
			update deviceToCloudBlob
			   set syncedAt = unixepoch()
			 where ordinal = ?
		`,
		[
			ordinal,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('updateDeviceToCloudBlobSynced', r.error))
	}
	return ok()
}

export const deleteDeviceToCloudBlob = async (ordinal: number): Promise<Result<void, FusionError>> => {
	let r = await sqlRun(
		`
			delete from deviceToCloudBlob
			 where ordinal = ?
		`,
		[
			ordinal,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('deleteDeviceToCloudBlob', r.error))
	}
	return ok()
}

// call after successful re-sync of both appt and todo
export const deleteSyncedDeviceToCloudBlobs = async (upn: string, createdBefore: number): Promise<Result<void, FusionError>> => {
	// 20260425 delete where upn = '*' to clean up already synced files
	// that were previously stored this way
	let r = await sqlRun(
		`
			delete from deviceToCloudBlob
			 where upn in (?, '${CloudToDeviceBlobUpnAll}')
			   and syncedAt is not null
			   and createdAt < ?
		`,
		[
			upn,
			createdBefore,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('deleteSyncedDeviceToCloudBlobs', r.error))
	}
	return ok()
}

// cleanup pending blobs on #splode
export const deleteAllDeviceToCloudBlobs = async (upn: string): Promise<Result<void, FusionError>> => {
	let r = await sqlRun(
		`
			delete from deviceToCloudBlob
			 where upn = ?
		`,
		[
			upn,
		],
	)
	if(r.isErr()) {
		return err(serializeFusionError('deleteSyncedDeviceToCloudBlobs', r.error))
	}
	return ok()
}

//
//
// Database Browser
//

export const selectDeviceToCloudBlobStrDiscriminators = async (): Promise<Result<string[], FusionError>> => {
	let r = await sqlQuery<{discriminatorStr: string}>(
		`
			select concat_ws('|', ordinal, type, encounterGuid, fileId, upn, syncedAt) as discriminatorStr
			  from deviceToCloudBlob
		`
	)
	return r.isErr() ? err(r.error) : ok(r.value.map(row => row.discriminatorStr))
}

export const selectDeviceToCloudBlobPreview = async (discriminatorStr: string): Promise<string> => {
	const [ordinalStr] = discriminatorStr.split('|')
	const ordinal = parseInt(ordinalStr)
	let r = await sqlQuery<{data: string|Uint8Array, encoding: LdbBlobStorageEncoding}>(
		`
			select data, encoding
			  from deviceToCloudBlob
			 where ordinal = ?
		`,
		[
			ordinal,
		],
	)
	if(r.isErr()) {
		return serializePrettyJsonFusionError('selectDeviceToCloudBlobPreview', r.error)
	} else if(r.value.length < 1) {
		return serializePrettyJsonFusionError('selectDeviceToCloudBlobPreview', 'record not found')
	}
	const row = r.value[0]
	return ldbReadBlobPrettyJson(row.data, row.encoding)
}
